import type { AuthSession, AuthUser } from './types';

/**
 * The access token lives here, in memory only. It is never written to
 * localStorage, sessionStorage or a JS-readable cookie, so an XSS bug cannot
 * read it from storage, and it disappears when the tab closes. The session
 * itself survives reloads through the httpOnly refresh cookie.
 */
export type SessionEvent = { type: 'session'; user: AuthUser } | { type: 'cleared'; reason: 'logout' | 'expired' };

type Listener = (event: SessionEvent) => void;

let accessToken: string | null = null;
let expiresAt = 0;
let user: AuthUser | null = null;
const listeners = new Set<Listener>();

const emit = (event: SessionEvent) => listeners.forEach((listener) => listener(event));

export const tokenStore = {
  getAccessToken: (): string | null => accessToken,
  getUser: (): AuthUser | null => user,
  /** Epoch ms when the access token expires (0 when signed out). */
  getExpiresAt: (): number => expiresAt,

  /** Milliseconds of validity left, or 0. */
  remainingMs: (): number => (accessToken ? Math.max(0, expiresAt - Date.now()) : 0),

  set(session: AuthSession, now = Date.now()): void {
    accessToken = session.accessToken;
    expiresAt = now + session.expiresIn * 1000;
    user = session.user;
    emit({ type: 'session', user: session.user });
  },

  /** Keeps the token but updates the user (e.g. after editing the profile). */
  updateUser(next: AuthUser): void {
    if (!accessToken) return;
    user = next;
    emit({ type: 'session', user: next });
  },

  clear(reason: 'logout' | 'expired'): void {
    const wasSignedIn = accessToken !== null;
    accessToken = null;
    expiresAt = 0;
    user = null;
    if (wasSignedIn || reason === 'expired') emit({ type: 'cleared', reason });
  },

  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};
